import React, {Fragment} from 'react'
import {useParams, Link} from 'react-router-dom'
import {formatDate, formatNumber, toPagination} from 'admin/lib/format'
import useDocument from 'admin/hooks/useDocument'
import useModal from 'admin/hooks/useModal'
import Pagination from 'admin/components/shared/Pagination'
import InputField from 'admin/components/shared/InputField'
import usePayments from 'admin/hooks/usePayments'
import Page from './shared/Page'
import DateField from './shared/DateField'
import Sorting from './shared/Sorting'
import {compact} from 'lodash'

const sortOptions = [
  {
    sortBy: 'payments.date',
    sortDirection: 'desc',
    sortLabel: 'Date received',
    ascLabel: 'Oldest first',
    descLabel: 'Latest first'
  },
  {sortBy: 'payments.statement_details', sortDirection: 'asc', sortLabel: 'Details', ascLabel: 'A-Z', descLabel: 'Z-A'},
  {
    sortBy: 'payments.statement_particulars',
    sortDirection: 'asc',
    sortLabel: 'Particulars',
    ascLabel: 'A-Z',
    descLabel: 'Z-A'
  },
  {sortBy: 'payments.statement_code', sortDirection: 'asc', sortLabel: 'Code', ascLabel: 'A-Z', descLabel: 'Z-A'},
  {
    sortBy: 'payments.statement_reference',
    sortDirection: 'asc',
    sortLabel: 'Reference',
    ascLabel: 'A-Z',
    descLabel: 'Z-A'
  },
  {sortBy: 'amount', sortDirection: 'asc', sortLabel: 'Amount'}
]

const Table = ({onClick, data, organisationId}) => {
  return (
    <table className='table is-striped is-hoverable is-fullwidth is-linked'>
      <colgroup>
        <col width='13%' />
        <col width='auto' />
        <col width='auto' />
        <col width='auto' />
        <col width='auto' />
        <col width='13%' />
        <col width='22%' />
      </colgroup>
      <thead>
        <tr>
          <th>Date received</th>
          <th>Details</th>
          <th>Particulars</th>
          <th>Code</th>
          <th>Reference</th>
          <th className='has-text-right'>Amount</th>
          <th>Account</th>
        </tr>
      </thead>

      <tbody>
        {data.map((payment, i) => {
          return (
            <tr
              key={i}
              onClick={e => {
                if (
                  e.target.tagName.toLowerCase() !== 'button' &&
                  e.target.tagName.toLowerCase() !== 'a' &&
                  e.target.type !== 'checkbox'
                ) {
                  e.preventDefault()
                  onClick({accountId: payment.account?.id, paymentId: payment.id})
                }
              }}>
              <th>{formatDate(payment.date)}</th>
              <td>{payment.importDetails}</td>
              <td>{payment.importParticulars}</td>
              <td>{payment.importCode}</td>
              <td>{payment.importReference}</td>
              <th className='has-text-right'>{formatNumber(payment.amount)}</th>
              <td>
                <div className='is-flex is-justify-content-space-between'>
                  <div>
                    <Link to={`/orgs/${organisationId}/accounts/${payment.account?.id}`}>
                      {payment.account?.displayName}
                    </Link>
                    {payment.account && payment.account.displayName !== payment.account.contactFullName ? (
                      <sub style={{whiteSpace: 'pre-line'}}>{payment.account?.contactFullName}</sub>
                    ) : null}
                  </div>
                  <div className='ml-1'>
                    {payment.xeroLink && (
                      <span className='tag is-small has-text-white' style={{backgroundColor: '#51afd8'}}>
                        X
                      </span>
                    )}
                    {payment.isDuplicate && <span className='tag is-small is-danger'>D</span>}
                  </div>
                </div>
              </td>
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

export default () => {
  const {organisationID, state} = useParams()
  const {pushModal} = useModal()
  const {payments, totalCount, filters, setFilters} = usePayments(state)

  const title = `Manage ${state || 'all'} payments`

  useDocument({title})

  return (
    <Page
      title={title}
      subtitle={
        totalCount
          ? `Showing ${toPagination(payments.length, totalCount, compact([state, 'payment']).join(' '))}`
          : `No ${state ? `${state} ` : ''}payments found`
      }>
      <form className='box is-radiusless is-shadowless has-background-filters'>
        <div className='field is-grouped'>
          <div className='control is-expanded'>
            <InputField
              type='search'
              name='q'
              label='Search'
              autoComplete='off'
              value={filters.q}
              debounce={true}
              onChange={changes => setFilters({...changes, page: 1})}
            />
          </div>
          <div className='control'>
            <DateField name='fromDate' value={filters.fromDate} onChange={setFilters} />
          </div>
          <div className='control'>
            <DateField name='toDate' value={filters.toDate} onChange={setFilters} />
          </div>
        </div>
      </form>

      {payments.length != 0 ? (
        <Fragment>
          <div className='level'>
            <div className='level-left'>
              <Sorting
                className='level-item'
                sortOptions={sortOptions}
                sortBy={filters.sortBy}
                sortDirection={filters.sortDirection}
                onChange={setFilters}
              />
            </div>
            <div className='level-right'>
              <Pagination
                page={filters.page}
                limit={filters.limit}
                total={totalCount}
                onChange={setFilters}
                className='level-item'
              />
            </div>
          </div>

          <Table data={payments} organisationId={organisationID} onClick={data => pushModal('payment', data)} />

          {payments.length >= 10 && (
            <div className='level'>
              <div className='level-left'></div>
              <div className='level-right'>
                <Pagination
                  page={filters.page}
                  limit={filters.limit}
                  total={totalCount}
                  onChange={setFilters}
                  className='level-item'
                />
              </div>
            </div>
          )}
        </Fragment>
      ) : null}
    </Page>
  )
}
